/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryDefinitionAPI',
	'./QueryDataItemSet'
], (_, APIFactory, QueryDefinitionAPI, QueryDataItemSet) => {

	const reducer = (accumulator, currentValue) => {
		accumulator[currentValue.columnId] = currentValue;
		return accumulator;
	};

	class QueryDefinition {

		constructor({ id, type }) {
			this.id = id;
			this.type = type;
			this.dataSource = null;
			this.dataItemSets = [];
			this.filters = [];
			this.summariesEnabled = true;	//Summaries are enabled by default.
			this.queryHints = [];
			this.expandEnabled = true;
			this.suppressionEnabled = true; //suppression is enabled by default.
			this.supportsNonContextualTopBottom = true; //Non contextual top bottom is supported by default.
			this.properties = {};
			this.parameterValues = [];
		}

		getId() {
			return this.id;
		}

		getType() {
			return this.type;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryDefinitionAPI]);
			}
			return this.api;
		}

		setDataSource(source) {
			this.dataSource = source;
		}

		getDataSource() {
			return this.dataSource;
		}

		setSummariesEnabled(flag) {
			this.summariesEnabled = flag;
		}

		getSummariesEnabled() {
			return this.summariesEnabled;
		}

		setSuppressionEnabled(flag) {
			this.suppressionEnabled = flag;
		}

		getSuppressionEnabled() {
			return this.suppressionEnabled;
		}

		createDataItemSet(dataItems) {
			const dataItemSet = new QueryDataItemSet();
			dataItemSet.addDataItemList(dataItems);
			this.dataItemSets.push(dataItemSet);
			return dataItemSet.getAPI();
		}

		getDataItemSetList() {
			return this.dataItemSets.slice();
		}

		getAllDataItemsList() {
			return _.flatten(this.dataItemSets.map(
				dataItemSet => dataItemSet.getAllDataItemsList()
			));
		}

		addFilters(filters) {
			this.filters.push(...filters);
		}

		addParameterValues(parameterValues) {
			this.parameterValues = parameterValues;
		}

		getParameterValues() {
			return this.parameterValues;
		}

		removeFilters(filtersToRemove = []) {
			if (filtersToRemove.length > 0) {
				const removeFiltersMap = filtersToRemove.reduce(reducer, {});
				const currentFilters = this.filters.reduce(reducer, {});
				const currentKeys = Object.keys(currentFilters);

				this.filters.length = 0;
				currentKeys.forEach(key => {
					if (!removeFiltersMap[key]) {
						this.filters.push(currentFilters[key]);
					}
				});
			}
		}

		getFilters() {
			return this.filters;
		}

		addQueryHint(queryHint) {
			this.queryHints.push(queryHint);
		}

		getQueryHints() {
			return this.queryHints;
		}

		setExpandEnabled(flag) {
			this.expandEnabled = flag;
		}

		getExpandEnabled() {
			return this.expandEnabled;
		}

		setSupportsNonContextualTopBottom(flag) {
			this.supportsNonContextualTopBottom = flag;
		}

		getSupportsNonContextualTopBottom() {
			return this.supportsNonContextualTopBottom;
		}

		getPropertyValue(name) {
			return this.properties[name];
		}

		setPropertyValue(name, value) {
			if (name) {
				if (value === undefined) {
					delete this.properties[name];
				} else {
					this.properties[name] = value;
				}
			}
		}
	}

	return QueryDefinition;

});
